import axios from './axiosInstance';
import axiosLib from 'axios';
import { APITrail } from '../../types';

interface SportAreaParams {
  sport: string;
  areaId: string;
}

interface GetTrailParams extends SportAreaParams {
  trailId: string;
}

interface GetTrailsParams extends SportAreaParams {
  abortSignal?: AbortSignal;
}

interface GetHistoricalTrailsParams extends SportAreaParams {
  timestamp: number;
}

interface GetCsvParams extends SportAreaParams {
  timestamp: number;
  fileName: string;
}

interface ParseFileParams extends SportAreaParams {
  jsFile: File;
}

interface CreateTrailParams extends SportAreaParams {
  name: string;
  difficulty?: string;
  geojson?: string;
  description?: string;
  /** Optional idempotency key — the PUT create route runs the backend's
   *  IdempotentRequests middleware, keyed on the x-request-id header. */
  requestId?: string;
}

interface UpdateTrailParams extends SportAreaParams {
  trailId: string;
  name?: string;
  difficulty?: string;
  description?: string;
  status?: string;
  groomed?: string;
  allowedActivities?: string[];
  timestamp?: number;
  geojson?: string;
}

interface UpdateMultipleTrailsParams extends SportAreaParams {
  trails: Partial<APITrail>[];
}

interface ArchiveTrailParams extends SportAreaParams {
  trailId: string;
}

const getTrail = ({ sport, areaId, trailId }: GetTrailParams): Promise<APITrail | void> => {
  let url = `/${sport}/areas/${areaId}/trails/${trailId}`;
  return axios.get(url)
    .then((response) => {
      const trail = response.data;
      return trail;
    })
    .catch((error) => {
      console.log(error);
    });
};

const getTrails = ({ sport, areaId, abortSignal }: GetTrailsParams): Promise<APITrail[]> => {
  const CancelToken = axiosLib.CancelToken;
  const source = CancelToken.source();
  if (abortSignal) {
    abortSignal.addEventListener('abort', () => {
      source.cancel('Operation canceled by the user.');
    });
  }

  let url = `/${sport}/areas/${areaId}/trails`;
  return axios.get(url, { cancelToken: source.token })
    .then((response) => {
      const trails = response.data;
      return trails;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getHistoricalTrails = ({ sport, areaId, timestamp }: GetHistoricalTrailsParams): Promise<APITrail[]> => {
  let url = `/${sport}/areas/${areaId}/trails/history?timestamp=${timestamp}`;
  return axios.get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getCsv = ({ sport, areaId, timestamp, fileName }: GetCsvParams): Promise<void> => {
  let url = `/${sport}/areas/${areaId}/trails/history?timestamp=${timestamp}&response=csv`;
  return axios.get(url)
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}.csv`); //or any other extension
      document.body.appendChild(link);
      link.click();
    })
    .catch((error) => {
      console.log(error);
    });
};

const parseFile = ({ sport, areaId, jsFile }: ParseFileParams): Promise<any> => {
  const url = `/${sport}/areas/${areaId}/trails/parsefile`;
  const formData = new FormData();
  formData.append("file", jsFile);
  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    }
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const createTrail = ({ sport, areaId, name, difficulty, geojson, description, requestId }: CreateTrailParams): Promise<APITrail> => {
  let url = `/${sport}/areas/${areaId}/trails`;
  return axios.put(url, {
    name,
    difficulty,
    geojson,
    description,
  }, {
    headers: {
      ...(requestId ? { 'x-request-id': requestId } : {}),
    },
  })
    .then((response) => {
      const trail = response.data;
      return trail;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const updateTrail = ({
  sport,
  areaId,
  trailId,
  name,
  difficulty,
  description,
  status,
  groomed,
  allowedActivities,
  timestamp,
  geojson,
}: UpdateTrailParams): Promise<APITrail> => {
  let url = `/${sport}/areas/${areaId}/trails/${trailId}`;
  return axios.post(url, {
    name,
    difficulty,
    description,
    status,
    groomed,
    allowedActivities,
    timestamp,
    geojson,
  })
    .then((response) => {
      const trail = response.data;
      return trail;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const updateMultipleTrails = ({
  sport,
  areaId,
  trails,
}: UpdateMultipleTrailsParams): Promise<APITrail[]> => {
  let url = `/${sport}/areas/${areaId}/trails`;
  return axios.post(url, trails)
    .then((response) => {
      const trails = response.data;
      return trails;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const archiveTrail = ({
  sport,
  areaId,
  trailId,
}: ArchiveTrailParams): Promise<APITrail> => {
  let url = `/${sport}/areas/${areaId}/trails/${trailId}/archive`;
  return axios.post(url, {})
    .then((response) => {
      const trail = response.data;
      return trail;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
}

const featureFunctions = {
  createTrail,
  getTrail,
  getTrails,
  getHistoricalTrails,
  getCsv,
  parseFile,
  updateTrail,
  updateMultipleTrails,
  archiveTrail,
};

export default featureFunctions;
